// src/App.tsx
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import ClaimsTable from './components/ClaimsTable';
import Login from './pages/Login'; // Corrected import
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { LenderRoute } from './components/LenderRoute';
import Header from './components/Header';
import AdminDashboard from './components/AdminDashboard'; // Import AdminDashboard
import CafCorpusManager from './components/CafCorpusManager';
import ClaimManagement from './components/ClaimManagement'; // Import ClaimManagement
import Dashboard from './components/Dashboard'; // Import Dashboard
import LenderHub from './pages/LenderHub';
import LenderClaimsExport from './components/LenderClaimsExport';
import EmailHub from './pages/EmailHub';
import LegacyClaims from './pages/LegacyClaims';
import AcceptInvitation from './pages/AcceptInvitation';
import ResetPassword from './pages/ResetPassword';
import Clients from './pages/Clients';
import ClientDetail from './pages/ClientDetail';
import PhotoIdTasks from './pages/PhotoIdTasks';
import { ClientLane } from './pages/lanes/ClientLane';
import { LenderLane } from './pages/lanes/LenderLane';
import { ManualLane } from './pages/lanes/ManualLane';
import { DashboardPage } from './pages/lanes/DashboardPage';
import ArchivedTasks from './pages/ArchivedTasks';
import { IDVerificationTasks } from './pages/IDVerificationTasks';
import { PostTasks } from './pages/PostTasks';
import { CallTasks } from './pages/CallTasks';
import Leads from './pages/Leads';
import AdminPaymentsPage from './pages/AdminPaymentsPage';
import AuditAnalysis from './pages/AuditAnalysis';
import CreateClaim from './pages/CreateClaim';
import ComplexAIChat from './pages/ComplexAIChat';
import CreditCheckLookup from './pages/CreditCheckLookup';
import EmailActivityPage from './components/EmailActivityPage';
import ComplexClaimsPage from './components/ComplexClaimsPage';
import CafInbox from './pages/CafInbox';
import './App.css';

export default function App() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isAcceptInvitationPage = location.pathname.startsWith('/accept-invitation');
  const isResetPasswordPage = location.pathname.startsWith('/reset-password');
  const hideHeader = isLoginPage || isAcceptInvitationPage || isResetPasswordPage;

  const routes = (
    <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/" element={
              <ProtectedRoute>
                <ClaimsTable />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/audit-analysis" element={
              <ProtectedRoute>
                <AdminRoute>
                  <AuditAnalysis />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/cancellation-payments" element={
              <ProtectedRoute>
                <AdminRoute allowedRoles={['payments-manager']}>
                  <AdminPaymentsPage />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/ai-curation" element={
              <ProtectedRoute>
                <AdminRoute allowedRoles={['corpus-curator', 'workflow-publisher']}>
                  <CafCorpusManager />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/*" element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/lenders" element={
              <ProtectedRoute>
                <LenderRoute>
                  <LenderHub />
                </LenderRoute>
              </ProtectedRoute>
            } />
            <Route path="/admin/lender-export" element={
              <ProtectedRoute>
                <AdminRoute>
                  <LenderClaimsExport />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/claim-management" element={
              <ProtectedRoute>
                <ClaimManagement />
              </ProtectedRoute>
            } />
            <Route path="/email" element={
              <ProtectedRoute>
                <AdminRoute>
                  <EmailHub />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/legacy-claims" element={
              <ProtectedRoute>
                <LegacyClaims />
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute>
                <Clients />
              </ProtectedRoute>
            } />
            <Route path="/clients/:clientId" element={
              <ProtectedRoute>
                <ClientDetail />
              </ProtectedRoute>
            } />
            <Route path="/admin/photo-id" element={
              <ProtectedRoute>
                <AdminRoute>
                  <PhotoIdTasks />
                </AdminRoute>
              </ProtectedRoute>
            } />
            {/* Tasks Rebuild (Task 8): /tasks redirects into the lane pages.
                Task 14 hard cutover: the legacy Tasks.tsx page and its
                /tasks/legacy route are deleted outright -- no ?classic
                escape. */}
            <Route path="/tasks" element={<Navigate to="/tasks/client" replace />} />
            <Route path="/tasks/dashboard" element={
              <ProtectedRoute>
                <AdminRoute>
                  <DashboardPage />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/tasks/client" element={
              <ProtectedRoute>
                <ClientLane />
              </ProtectedRoute>
            } />
            <Route path="/tasks/lender" element={
              <ProtectedRoute>
                <LenderLane />
              </ProtectedRoute>
            } />
            {/* Post lane tab removed from the Tasks rebuild -- the dedicated
                /post-tasks page remains the postal workflow. Deep links to
                the old /tasks/post lane redirect there. */}
            <Route path="/tasks/post" element={<Navigate to="/post-tasks" replace />} />
            <Route path="/tasks/manual" element={
              <ProtectedRoute>
                <ManualLane />
              </ProtectedRoute>
            } />
            <Route path="/id-verification" element={
              <ProtectedRoute>
                <IDVerificationTasks />
              </ProtectedRoute>
            } />
            <Route path="/post-tasks" element={
              <ProtectedRoute>
                <PostTasks />
              </ProtectedRoute>
            } />
            <Route path="/archived-tasks" element={
              <ProtectedRoute>
                <AdminRoute allowedRoles={['archive-viewer']}>
                  <ArchivedTasks />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/call-tasks" element={
              <ProtectedRoute>
                <AdminRoute>
                  <CallTasks />
                </AdminRoute>
              </ProtectedRoute>
            } />
            <Route path="/leads" element={
              <ProtectedRoute>
                <Leads />
              </ProtectedRoute>
            } />
            <Route path="/ai-chat" element={
              <ProtectedRoute>
                <ComplexAIChat />
              </ProtectedRoute>
            } />
            <Route path="/create-claim" element={
              <ProtectedRoute>
                <CreateClaim />
              </ProtectedRoute>
            } />
            <Route path="/credit-checks" element={
              <ProtectedRoute>
                <CreditCheckLookup />
              </ProtectedRoute>
            } />
            <Route path="/email-activity" element={
              <ProtectedRoute>
                <EmailActivityPage />
              </ProtectedRoute>
            } />
            <Route path="/complex-claims" element={
              <ProtectedRoute>
                <ComplexClaimsPage />
              </ProtectedRoute>
            } />
            <Route path="/caf-inbox" element={
              <ProtectedRoute>
                <CafInbox />
              </ProtectedRoute>
            } />
            <Route path="/ui" element={<Navigate to="/" replace />} />
    </Routes>
  );

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <div className={hideHeader ? 'app-container' : 'app-shell'}>
          {!hideHeader && <Header />}
          <main className={hideHeader ? '' : 'app-content'}>
            {hideHeader ? routes : <div className="app-content-inner">{routes}</div>}
          </main>
        </div>
      </AuthProvider>
    </QueryClientProvider>
  );
}
